import { useEffect, useRef, useState } from 'react'
import { AppBar, Box, Toolbar, Button, Menu, Tooltip } from '@mui/material'
import { t } from 'i18next'
import { useSelector } from 'react-redux'

import {
  headerStyles as styles,
  SpanUsername,
  SpanCollegiate
} from 'src/components/Layout/components/Header/HeaderStyles'
import ConnectionChip from 'src/components/Layout/components/Header/components/ConnectionChip/ConnectionChip'
import HeaderMenu from 'src/components/Layout/components/Header/components/HeaderMenu/HeaderMenu'
import AlertDialog from 'src/components/shared/ui/AlertDialog/AlertDialog'
import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import {
  CloseRoundedFilledIcon,
  HelpIcon,
  ProfileIcon,
  RoundCheckFilledIcon,
  UserFilledIcon
} from 'src/components/shared/ui/Icons'
import SelectUserDialog from 'src/components/shared/ui/SelectUserDialog/SelectUserDialog'
import ToolTipEllipsis from 'src/components/shared/ui/TooltipEllipsis'
import { MY_ACCOUNT_URL } from 'src/config'
import { useAlertContext } from 'src/contexts/AlertContext'
import { useLexnetStatusContext } from 'src/contexts/LexnetStatusContext'
// import LexNetUserStatus from 'src/components/Layout/components/Header/components/LexNetUserStatus/LexNetUserStatus'

import { theme } from 'src/providers/ThemeProvider'
import { userSelector, isAuthenticatedSelector } from 'src/state/authentication/selectors'
// import ButtonPrimary from 'src/components/shared/ui/ButtonPrimary'
import Help from './components/Help/Help'
import SelectProfile from './components/SelectProfile/SelectProfile'

type Props = {
  onConnect: () => void
  onDisconnect: () => Promise<void>
  lexnetConnected: boolean
}

export default function Header({ lexnetConnected, onConnect, onDisconnect }: Props): JSX.Element {
  const user = useSelector(userSelector)
  const { setLoggedUser, getLexnetUserText } = useLexnetStatusContext()
  const isUserAuthenticated = useSelector(isAuthenticatedSelector)
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false)
  const [openMenu, setOpenMenu] = useState<boolean>(false)
  // const [openMenuConfiguration, setOpenMenuConfiguration] = useState<boolean>(false)
  const [openHelpModal, setOpenHelpModal] = useState<boolean>(false)
  // const [openMyAccount, setOpenMyAccount] = useState<boolean>(false)
  const [showDialogUser, setShowDialogUser] = useState(false)
  const anchorEl = useRef<HTMLDivElement>(null)
  // const anchorElConfiguration = useRef<HTMLButtonElement>(null)
  const anchorElMyAccount = useRef<HTMLDivElement>(null)
  const { addAlert } = useAlertContext()

  useEffect(() => {
    if (!lexnetConnected) {
      setOpenMenu(false)
      // setOpenMenuConfiguration(false)
    }
  }, [lexnetConnected])

  function onClickConnection() {
    if (lexnetConnected) {
      setConfirmLogoutOpen(true)
    } else {
      onConnect()
    }

    setOpenMenu(false)
    // setOpenMenuConfiguration(false)
  }

  function menuHandleClick() {
    setOpenMenu(prevOpen => !prevOpen)
  }

  // function menuConfigurationHandleClick() {
  //   if (lexnetConnected) {
  //     setOpenMenuConfiguration(prevOpen => !prevOpen)
  //   }
  // }

  async function onSelectUser(user: any) {
    await setLoggedUser(user)
    setShowDialogUser(false)
  }

  return (
    <AppBar position="fixed" elevation={0} sx={styles.appBar}>
      <Toolbar sx={styles.toolbar}>
        <Box sx={styles.logo} component="div" display="flex" alignItems="center">
          <img
            src="https://assets.lefebvre.es/media/logos-2/png/lefebvre-205x56.png"
            alt="Lefebvre"
            width="139"
          />
        </Box>
        <Box component="div" sx={styles.menuBox}>
          <HeaderMenu />
        </Box>
        <ConnectionChip
          isConnectedToLexnet={lexnetConnected}
          isUserAuthenticated={isUserAuthenticated}
          onClick={onClickConnection}
        />

        {/* <Tooltip title="Configuración" arrow>
          <span>
            <Button
              sx={styles.configuration}
              onClick={menuConfigurationHandleClick}
              ref={anchorElConfiguration}
              disabled={!lexnetConnected}
            >
              <ConfigurationIcon />
            </Button>
          </span>
        </Tooltip>

        <Menu
          id="configurationLogin"
          anchorEl={anchorElConfiguration.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          open={openMenuConfiguration}
          sx={styles.userMenu}
          onClose={() => setOpenMenuConfiguration(false)}
        >
          <Box sx={styles.userMenuConnection}>
            <SelectProfile
              onClick={() => {
                setOpenMenuConfiguration(false)
                setShowDialogUser(true)
              }}
            />
            <ConnectionChip
              isConnectedToLexnet={lexnetConnected}
              isUserAuthenticated={isUserAuthenticated}
              onClick={onClickConnection}
            />
          </Box>
        </Menu> */}

        <Box sx={styles.separator} />

        {isUserAuthenticated && (
          <>
            <Box
              component="div"
              sx={styles.user}
              data-connected={lexnetConnected}
              onClick={menuHandleClick}
              ref={anchorEl}
            >
              <Box sx={styles.userAvatar}>
                <ProfileIcon width={32} height={32} color={theme.colors.gray700} />

                <Box sx={styles.userAvatarIcon}>
                  {lexnetConnected ? (
                    <RoundCheckFilledIcon width={12} height={12} color={theme.colors.seaGreen200} />
                  ) : (
                    <CloseRoundedFilledIcon width={12} height={12} color={theme.colors.gray16} />
                  )}
                </Box>
              </Box>
              <Box display="flex" flexDirection="column" sx={{ maxWidth: '150px' }}>
                <SpanUsername>{user?.name}</SpanUsername>

                <Tooltip arrow title={getLexnetUserText(user)} placement="bottom">
                  <SpanCollegiate>{getLexnetUserText(user)}</SpanCollegiate>
                </Tooltip>
              </Box>
            </Box>

            <Menu
              id="menuLogin"
              anchorEl={anchorEl.current}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              open={openMenu}
              sx={styles.userMenu}
              onClose={() => setOpenMenu(false)}
            >
              <Box sx={styles.userMenuName}>{user?.name}</Box>
              {/* <Box sx={styles.userMenuCompany}>{user?.name}</Box> */}
              <ToolTipEllipsis text={getLexnetUserText(user)}>
                <Box sx={styles.userMenuCollegiate}>{getLexnetUserText(user)}</Box>
              </ToolTipEllipsis>
              <Box sx={styles.userMenuSeparator} />
              <Box
                sx={styles.account}
                ref={anchorElMyAccount}
                // onClick={() => setOpenMyAccount(true)}
                // onMouseOver={() => setOpenMyAccount(true)}
              >
                <Box sx={styles.accountWrapperIcon} alignSelf="center">
                  <UserFilledIcon width={16} height={16} color={theme.corporate.primaryColor} />
                </Box>
                <Box
                  sx={styles.myAccountText}
                  flex="1 1 auto"
                  onClick={() => {
                    window.open(`${MY_ACCOUNT_URL}${user?.encryptedLexonId}`, '_blank')
                  }}
                >
                  {t('components.menu_user.my_account')}
                </Box>
              </Box>
              {lexnetConnected && (
                <Box sx={{ marginTop: '12px' }}>
                  <SelectProfile
                    onClick={() => {
                      // setOpenMenuConfiguration(false)
                      setOpenMenu(false)
                      setShowDialogUser(true)
                    }}
                  />
                </Box>
              )}

              {/* <Menu
                id="menuMyAccount"
                anchorEl={anchorElMyAccount.current}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left'
                }}
                open={openMyAccount}
                sx={styles.userMenu}
                onClose={() => setOpenMyAccount(false)}
                MenuListProps={{ onMouseLeave: () => setOpenMyAccount(false) }}
              >
                <ButtonBase sx={styles.subMenuItem}>
                  <UserIcon className="iconUser" />
                  {t('components.menu_user.my_personal_info')}
                </ButtonBase>
                <ButtonBase sx={styles.subMenuItem}>
                  <BillIcon />
                  {t('components.menu_user.my_bills')}
                </ButtonBase>
                <ButtonBase sx={styles.subMenuItem}>
                  <ContactsIcon />
                  {t('components.menu_user.contact_directory')}
                </ButtonBase>
              </Menu> */}
            </Menu>
          </>
        )}
        <Tooltip title="Ayuda" arrow>
          <Button sx={styles.help} onClick={() => setOpenHelpModal(true)}>
            <HelpIcon width={26} height={26} color={theme.colors.gray700} />
          </Button>
        </Tooltip>
      </Toolbar>
      {/* <LexNetUserStatus /> */}

      <AlertDialog
        show={confirmLogoutOpen}
        type={AlertType.INFO}
        message={t('alert.confirm_logout')}
        close={() => setConfirmLogoutOpen(false)}
        accept={async () => {
          await onDisconnect()
          addAlert({
            message: t('alert.successful_logout'),
            type: AlertType.SUCCESS
          })
        }}
        cancel={() => setConfirmLogoutOpen(false)}
        primaryButtonText={t('actions.logout')}
        secondaryButtonText={t('actions.cancel')}
      />

      <Help
        onClose={() => {
          setOpenHelpModal(false)
        }}
        open={openHelpModal}
      />

      {showDialogUser && (
        <SelectUserDialog
          show={showDialogUser}
          close={() => setShowDialogUser(false)}
          select={onSelectUser}
          disabledButton={false}
        />
      )}
    </AppBar>
  )
}
